import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      const isGuest = localStorage.getItem('guest_mode') === 'true';
      window.location.href = isGuest ? '/guest/login' : '/login';
    }
    return Promise.reject(error);
  }
);

// Public axios instance — used by token-authenticated endpoints (Readiness respondent
// page, etc.) where the caller is an EXTERNAL user WITHOUT a platform account.
// No Authorization header, no 401 redirect.
export const publicApi = axios.create({
  baseURL: `${API_URL}/api`,
});

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  getMyModules: () => api.get('/auth/me/modules'),
  requestPasswordReset: (email) => api.post('/auth/forgot-password', { email }),
  validateResetToken: (token) => api.post('/auth/validate-reset-token', { token }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  validateInviteToken: (token) => api.post('/auth/validate-invite-token', { token }),
  setInvitePassword: (token, password) => api.post('/auth/set-password', { token, password }),
  verifyEmailChange: (token) => api.post('/auth/verify-email-change', { token }),
};

// DPO Organizations (Task 5B: Seat licensing)
export const dpoOrganizationAPI = {
  getCurrent: () => api.get('/dpo-organizations/current'),
  getCurrentStats: () => api.get('/dpo-organizations/current/stats'),
};

// Organizations (Legacy - now consolidated into Clients)
// Use clientsAPI instead. This is kept for backward compatibility in UI references
export const organizationsAPI = {
  getAll: () => api.get('/clients'),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Clients (Multi-Tenant)
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getStats: (id) => api.get(`/clients/${id}/stats`),
  // Task 5B: Seat licensing (per-client per spec Section 5.2)
  getSeats: (id) => api.get(`/clients/${id}/seats`),
  // Task 5.4B: Super Admin seat override
  overrideSeats: (id, data) => api.put(`/clients/${id}/seats/override`, data),
  // Module management
  getModules: (id) => api.get(`/clients/${id}/modules`),
  updateModules: (id, modules) => api.put(`/clients/${id}/modules`, { enabled_modules: modules }),
  updateSubscription: (id, tier) => api.put(`/clients/${id}/subscription`, null, { params: { subscription_tier: tier } }),
  // Per "Fluid Module Assignment" spec: explicit reset of a client's enabled_modules
  // to the current template for their tier. Tier change alone does NOT reset modules.
  applyTierTemplate: (id) => api.post(`/clients/${id}/apply-tier-template`),
  updateSubdomain: (id, subdomain) => api.put(`/clients/${id}/subdomain`, null, { params: { subdomain } }),
  // Task 6: MFA per-client policy
  getMfaPolicy: (id) => api.get(`/super-admin/clients/${id}/mfa-policy`),
  setMfaPolicy: (id, data) => api.put(`/super-admin/clients/${id}/mfa-policy`, data),
  // Get client by subdomain (for portal)
  getBySubdomain: (subdomain) => api.get(`/clients/by-subdomain/${subdomain}`),
};

// Module Templates — DB-backed tier defaults (per "Fluid Module Assignment" spec).
// Super Admin only. Editing a tier's template only affects clients created AFTER
// the edit; existing clients keep their own enabled_modules until explicitly reset
// via clientsAPI.applyTierTemplate(clientId).
export const moduleTemplatesAPI = {
  getAll: () => api.get('/module-templates'),
  getOne: (tier) => api.get(`/module-templates/${tier}`),
  update: (tier, enabled_modules) => api.put(`/module-templates/${tier}`, { enabled_modules }),
};

// Entities (Multi-Tenant)
export const entitiesAPI = {
  getAll: (clientId) => api.get('/entities', { params: { client_id: clientId } }),
  getOne: (id) => api.get(`/entities/${id}`),
  create: (data) => api.post('/entities', data),
  update: (id, data) => api.put(`/entities/${id}`, data),
  delete: (id) => api.delete(`/entities/${id}`),
  getStats: (id) => api.get(`/entities/${id}/stats`),
};

// Departments
export const departmentsAPI = {
  getByEntity: (entityId) => api.get('/departments', { params: { entity_id: entityId } }),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
  sync: (entityId, name) => api.post('/departments/sync', { entity_id: entityId, name }),
};

// ROPA
export const ropaAPI = {
  getAll: (entityId, clientId, departmentId) => api.get('/ropa', { params: { entity_id: entityId, client_id: clientId, department_id: departmentId } }),
  getOne: (id) => api.get(`/ropa/${id}`),
  create: (data) => api.post('/ropa', data),
  update: (id, data) => api.put(`/ropa/${id}`, data),
  delete: (id) => api.delete(`/ropa/${id}`),
  // CSV Import
  downloadTemplate: () => api.get('/ropa/import/template', { responseType: 'blob' }),
  previewImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/ropa/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  confirmImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/ropa/import/confirm', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// ROPA Intake (Public tokenized intake forms)
export const ropaIntakeAPI = {
  // Authenticated (DPO)
  createToken: (data) => api.post('/ropa-intake/tokens', data),
  listTokens: (params) => api.get('/ropa-intake/tokens', { params }),
  revokeToken: (id) => api.post(`/ropa-intake/tokens/${id}/revoke`),
  resendEmail: (id) => api.post(`/ropa-intake/tokens/${id}/resend`),
  // Public (no auth - use axios directly)
  validateToken: (token) => axios.get(`${API_URL}/api/ropa-intake/validate/${token}`),
  submit: (token, data) => axios.post(`${API_URL}/api/ropa-intake/submit/${token}`, data),
};

// Data Breaches
export const breachesAPI = {
  getAll: (entityId, clientId) => api.get('/breaches', { params: { entity_id: entityId, client_id: clientId } }),
  getOne: (id) => api.get(`/breaches/${id}`),
  create: (data) => api.post('/breaches', data),
  update: (id, data) => api.put(`/breaches/${id}`, data),
  delete: (id) => api.delete(`/breaches/${id}`),
};

// Breach Reports (Workflow)
export const breachReportsAPI = {
  getAll: (entityId) => api.get('/breach-reports', { params: { entity_id: entityId } }),
  getOne: (id) => api.get(`/breach-reports/${id}`),
  create: (data) => api.post('/breach-reports', data),
  update: (id, data) => api.put(`/breach-reports/${id}`, data),
  delete: (id) => api.delete(`/breach-reports/${id}`),
};

// Breach Intake (Staff reporter form)
export const breachIntakeAPI = {
  // DPO endpoints (protected)
  create: (data) => api.post('/breach-intake/create', data),

  // Public endpoints (no auth)
  getPublic: (token) => api.get(`/breach-intake/public/${token}`),
  saveProgress: (token, data) => api.put(`/breach-intake/public/${token}/save`, data),
  submit: (token, data) => api.post(`/breach-intake/public/${token}/submit`, data),
};

// DSR Requests (Legacy)
export const dsrAPI = {
  getAll: (entityId) => api.get('/dsrs', { params: { entity_id: entityId } }),
  getOne: (id) => api.get(`/dsrs/${id}`),
  create: (data) => api.post('/dsrs', data),
  update: (id, data) => api.put(`/dsrs/${id}`, data),
  delete: (id) => api.delete(`/dsrs/${id}`),
};

// DPIA (Template-based Assessment)
export const dpiaAPI = {
  getAll: (entityId, clientId, departmentId) => api.get('/dpia', { params: { entity_id: entityId, client_id: clientId, department_id: departmentId } }),
  getOne: (id) => api.get(`/dpia/${id}`),
  create: (data) => api.post('/dpia', data),
  update: (id, data) => api.put(`/dpia/${id}`, data),
  delete: (id) => api.delete(`/dpia/${id}`),
  exportDocx: (id, exportType = 'full') => api.get(`/dpia/${id}/export`, { 
    params: { export_type: exportType },
    responseType: 'blob' 
  }),
};

// DPIA Intake (Questionnaire for clients)
export const dpiaIntakeAPI = {
  // DPO endpoints (protected)
  create: (data) => api.post('/dpia-intake/create', data),
  getList: (entityId, status, departmentId) => api.get('/dpia-intake/list', {
    params: { entity_id: entityId, status, department_id: departmentId }
  }),
  getById: (id) => api.get(`/dpia-intake/${id}`),
  saveById: (id, data) => api.put(`/dpia-intake/${id}/save`, data),
  submitById: (id, data) => api.post(`/dpia-intake/${id}/submit`, data),
  delete: (id) => api.delete(`/dpia-intake/${id}`),
  resendEmail: (id) => api.post(`/dpia-intake/${id}/resend-email`),
  sendTestEmail: () => api.post('/dpia-intake/test-email'),
  
  // Public endpoints (no auth — token is the gate)
  getPublic: (token) => api.get(`/dpia-intake/public/${token}`),
  saveProgress: (token, data) => api.put(`/dpia-intake/public/${token}/save`, data),
  submit: (token, data) => api.post(`/dpia-intake/public/${token}/submit`, data),

  // Supporting documents on the intake (spec §Supporting Document Upload)
  uploadDocument: (token, formData) => api.post(
    `/dpia-intake/public/${token}/upload-document`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  deleteDocument: (token, fileId) => api.delete(`/dpia-intake/public/${token}/document/${fileId}`),
  // Public (token-gated) download — submitter re-download
  publicDownloadDocumentUrl: (token, fileId) =>
    `${api.defaults.baseURL}/dpia-intake/public/${token}/document/${fileId}/download`,
  // DPO-authenticated download used from the DPIA detail view
  downloadDocument: (intakeId, fileId) => api.get(
    `/dpia-intake/${intakeId}/document/${fileId}/download`,
    { responseType: 'blob' }
  ),
};

// DSR V2 (Comprehensive Caribbean Multi-Jurisdiction)
export const dsrV2API = {
  getAll: (entityId, status, requestType, clientId) => api.get('/dsr/v2', {
    params: { entity_id: entityId, status, request_type: requestType, client_id: clientId }
  }),
  getOne: (id) => api.get(`/dsr/v2/${id}`),
  create: (data) => api.post('/dsr/v2', data),
  update: (id, data) => api.put(`/dsr/v2/${id}`, data),
  delete: (id) => api.delete(`/dsr/v2/${id}`),
  getOptions: () => api.get('/dsr/v2/config'),
  getStats: (entityId, clientId) => api.get('/dsr/v2/stats/summary', { params: { entity_id: entityId, client_id: clientId } }),
  addActivity: (id, action, notes) => api.post(`/dsr/v2/${id}/activity`, { action, notes }),
};

// Vendors (Legacy)
export const vendorsAPI = {
  getAll: (entityId) => api.get('/vendors', { params: { entity_id: entityId } }),
  getOne: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
};

// Vendor Management (Consolidated - all vendors in single table)
export const vendorV2API = {
  getAll: (entityId, lifecycleStage, riskTier) => api.get('/vendors', {
    params: { organization_id: entityId, lifecycle_stage: lifecycleStage, risk_tier: riskTier }
  }),
  getOne: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
  getStats: (entityId) => api.get('/vendors/stats/summary', { params: { organization_id: entityId } }),
  getDashboard: (entityId) => api.get('/vendors/stats/summary', { params: { organization_id: entityId } }),
};

// Simplified Vendor Management (Caribbean Focus) - USES CONSOLIDATED /vendors ENDPOINT
export const vendorSimplifiedAPI = {
  getAll: (entityId, filters = {}) => api.get('/vendors', {
    params: {
      organization_id: entityId,
      lifecycle_stage: filters.lifecycleStage,
      risk_tier: filters.riskTier,
      dpa_missing: filters.dpaMissing,
      expiring_days: filters.expiringDays
    }
  }),
  getOne: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
  getDashboard: (entityId) => api.get('/vendors/stats/summary', { params: { organization_id: entityId } }),
};

// Vendor Registry Dashboard (Spec v1.0 - New Dashboard Page)
// Uses consolidated /vendors endpoint (same as vendorSimplifiedAPI)
export const vendorRegistryAPI = {
  getAll: (entityId, clientId, filters = {}) => api.get('/vendors', {
    params: {
      organization_id: entityId || undefined,
      client_id: clientId || undefined,
      lifecycle_stage: filters.lifecycleStage || undefined,
      risk_tier: filters.riskTier || undefined,
    }
  }),
  getDashboard: (entityId, clientId) => api.get('/vendors/stats/summary', {
    params: { organization_id: entityId || undefined, client_id: clientId || undefined }
  }),
};

// Due Diligence Assessments (Separate from Vendor)
export const dueDiligenceAPI = {
  getAll: (entityId, clientId, filters = {}) => api.get('/due-diligence', {
    params: {
      entity_id: entityId,
      client_id: clientId,
      vendor_id: filters.vendorId,
      status: filters.status,
      outcome: filters.outcome
    }
  }),
  getOne: (id) => api.get(`/due-diligence/${id}`),
  create: (data) => api.post('/due-diligence', data),
  update: (id, data) => api.put(`/due-diligence/${id}`, data),
  delete: (id) => api.delete(`/due-diligence/${id}`),
  getVendorHistory: (vendorId) => api.get(`/due-diligence/vendor/${vendorId}/history`),
  getVendorLatest: (vendorId) => api.get(`/due-diligence/vendor/${vendorId}/latest`),
  getDashboard: (entityId) => api.get('/due-diligence/stats/dashboard', { params: { entity_id: entityId } }),
  exportDocx: (id) => api.get(`/due-diligence/${id}/export`, { responseType: 'blob' }),
};

// Vendor Risk Assessments (Question-Based Assessment)
export const vendorRiskAssessmentAPI = {
  create: (data) => api.post('/vendor-risk-assessments', data),
  getLatest: (vendorId) => api.get(`/vendor-risk-assessments/${vendorId}`),
  getHistory: (vendorId, limit = 10) => api.get(`/vendor-risk-assessments/${vendorId}/history`, { params: { limit } }),
  update: (assessmentId, data) => api.put(`/vendor-risk-assessments/${assessmentId}`, data),
  delete: (assessmentId) => api.delete(`/vendor-risk-assessments/${assessmentId}`),
};

// Risk Register
export const riskRegisterAPI = {
  getAll: (entityId) => api.get('/risk-register', { params: { entity_id: entityId } }),
  getOne: (id) => api.get(`/risk-register/${id}`),
  create: (data) => api.post('/risk-register', data),
  update: (id, data) => api.put(`/risk-register/${id}`, data),
  delete: (id) => api.delete(`/risk-register/${id}`),
};

// Risk Library
export const riskLibraryAPI = {
  getAll: (category, search) => api.get('/risk-library', { params: { category, search } }),
  getStats: () => api.get('/risk-library/stats'),
  create: (data) => api.post('/risk-library', data),
  update: (id, data) => api.put(`/risk-library/${id}`, data),
  delete: (id) => api.delete(`/risk-library/${id}`),
  seed: () => api.post('/risk-library/seed'),
};

// Control Library
export const controlLibraryAPI = {
  getAll: (category, controlId, search) => api.get('/control-library', { params: { category, control_id: controlId, search } }),
  getStats: () => api.get('/control-library/stats'),
  create: (data) => api.post('/control-library', data),
  update: (id, data) => api.put(`/control-library/${id}`, data),
  delete: (id) => api.delete(`/control-library/${id}`),
};

// Tasks
export const tasksAPI = {
  getAll: (entityId, status, sourceModule, search, assignedTo) => api.get('/tasks', { params: { entity_id: entityId, status, source_module: sourceModule, search, assigned_to: assignedTo } }),
  getSummary: () => api.get('/tasks/summary'),
  getOne: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  updateStatus: (id, status) => api.patch(`/tasks/${id}/status`, { status }),
  delete: (id) => api.delete(`/tasks/${id}`),
  getComments: (id) => api.get(`/tasks/${id}/comments`),
  getActivity: (id) => api.get(`/tasks/${id}/activity`),
  addComment: (id, data) => api.post(`/tasks/${id}/comments`, data),
  // Per-source-record helpers (spec Section 5 & 6)
  getCount: (sourceModule, sourceId) => api.get('/tasks/count', { params: { source_module: sourceModule, source_id: sourceId } }),
  getCountsBatch: (sourceModule) => api.get('/tasks/counts', { params: { source_module: sourceModule } }),
  getBySource: (sourceModule, sourceId) => api.get('/tasks', { params: { source_module: sourceModule, source_id: sourceId } }),
  getSuggestions: (sourceModule, sourceId) => api.get(`/tasks/suggestions/${sourceModule}/${sourceId}`),
  createBulk: (tasks) => api.post('/tasks/bulk', { tasks }),
  // Option B: aggregated tasks across all snapshots for a maturity entity
  getByAssessmentEntity: (entityId) => api.get(`/tasks/assessment-entity/${entityId}`),
};

// Documents
export const documentsAPI = {
  // entityFilter: pass an entity id to narrow, or "client_wide" to show only null/missing entity_id docs
  getAll: (entityId, clientId, limit = 50, skip = 0, entityFilter) =>
    api.get('/documents', {
      params: {
        client_id: clientId,
        entity_id: entityFilter,
        skip,
        limit,
      },
    }),
  getById: (id) => api.get(`/documents/${id}`),
  getOne: (id) => api.get(`/documents/${id}`),  // Alias for backwards compatibility
  create: (data) => api.post('/documents', data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  getOverdue: () => api.get('/documents/alerts/overdue'),
  uploadFile: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/documents/${id}/file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Data Flows (legacy)
export const dataFlowsAPI = {
  getAll: (entityId) => api.get('/data-flows', { params: { entity_id: entityId } }),
  getOne: (id) => api.get(`/data-flows/${id}`),
  create: (data) => api.post('/data-flows', data),
  update: (id, data) => api.put(`/data-flows/${id}`, data),
  delete: (id) => api.delete(`/data-flows/${id}`),
};

// Data Flow Map (visual canvas persistence)
export const dataflowMapAPI = {
  get: () => api.get('/dataflow'),
  save: (data) => api.post('/dataflow/save', data),
};

// Legitimate Interest Assessment (LIA)
export const liaAPI = {
  getAll: (entityId, clientId, status) => api.get('/lia', { params: { entity_id: entityId, client_id: clientId, status } }),
  getOne: (id) => api.get(`/lia/${id}`),
  create: (data) => api.post('/lia', data),
  update: (id, data) => api.put(`/lia/${id}`, data),
  delete: (id) => api.delete(`/lia/${id}`),
};

// Transfer Impact Assessment (TIA)
export const tiaAPI = {
  getAll: (entityId, clientId) => api.get('/tia', { params: { entity_id: entityId, client_id: clientId } }),
  getOne: (id) => api.get(`/tia/${id}`),
  create: (data) => api.post('/tia', data),
  update: (id, data) => api.put(`/tia/${id}`, data),
  delete: (id) => api.delete(`/tia/${id}`),
};

// MFA — Org Admin Management
export const exportAPI = {
  breachReportDocx: (reportId) => api.get(`/export/breach-reports/${reportId}/docx`, { responseType: 'blob' }),
  dsrDocx: (dsrId) => api.get(`/export/dsr/${dsrId}/docx`, { responseType: 'blob' }),
  ropaDocx: (ropaId) => api.get(`/export/ropa/${ropaId}/docx`, { responseType: 'blob' }),
  liaDocx: (liaId) => api.get(`/export/lia/${liaId}/docx`, { responseType: 'blob' }),
  tiaDocx: (tiaId) => api.get(`/export/tia/${tiaId}/docx`, { responseType: 'blob' }),
  dpiaDocx: (dpiaId) => api.get(`/dpia/${dpiaId}/export`, { responseType: 'blob' }),
  organizationFullExport: (orgId) => api.get(`/export/organization/${orgId}/full-export`, { responseType: 'blob' }),
  clientFullExport: (clientId) => api.get(`/export/client/${clientId}/full-export`, { responseType: 'blob' }),
  ropaXlsx: (clientId, entityId) => {
    const p = new URLSearchParams({ client_id: clientId });
    if (entityId) p.append('entity_id', entityId);
    return api.get(`/ropa/export/xlsx?${p.toString()}`, { responseType: 'blob' });
  },
  ropaPdf: (clientId, entityId) => {
    const p = new URLSearchParams({ client_id: clientId });
    if (entityId) p.append('entity_id', entityId);
    return api.get(`/ropa/export/pdf?${p.toString()}`, { responseType: 'blob' });
  },
};

export const mfaOrgAPI = {
  getUsers: () => api.get('/mfa/org/users'),
  resetUser: (userId) => api.post(`/mfa/org/users/${userId}/reset`),
  requireUser: (userId, data) => api.put(`/mfa/org/users/${userId}/require`, data),
  getPolicy: () => api.get('/mfa/org/policy'),
  updatePolicy: (data) => api.put('/mfa/org/policy', data),
  getEnforcementStatus: () => api.get('/mfa/org/enforcement-status'),
};

// TR2UST v2.0 Maturity Assessment API
export const maturityAPI = {
  // Configuration
  getConfig: () => api.get('/maturity/config'),
  getDomains: () => api.get('/maturity/domains'),
  getDomainControls: (domainCode, entityId) => api.get(`/maturity/domains/${domainCode}/controls`, { params: { entity_id: entityId } }),
  getControls: (domain, tier, entityId) => api.get('/maturity/controls', { params: { domain, tier, entity_id: entityId } }),
  getControl: (controlId) => api.get(`/maturity/controls/${controlId}`),
  
  // Tier Info (for filtering controls based on client's maturity tier)
  getTier: (entityId, clientId) => api.get(`/maturity/tier/${entityId}`, { params: { client_id: clientId } }),

  // Assessment Sessions
  createSession: (data) => api.post('/maturity/sessions', data),
  getSessions: (entityId, status, clientId) => api.get('/maturity/sessions', { params: { entity_id: entityId, status, client_id: clientId } }),

  // Control Assessments
  submitAssessment: (entityId, data) => api.post(`/maturity/assessments?entity_id=${entityId}`, data),
  getAssessments: (entityId, domain, clientId) => api.get('/maturity/assessments', { params: { entity_id: entityId, domain, client_id: clientId } }),
  getControlAssessment: (entityId, controlId) => api.get(`/maturity/assessments/${entityId}/${controlId}`),

  // Scoring
  getScore: (entityId, clientId) => api.get(`/maturity/score/${entityId}`, { params: { client_id: clientId } }),
  
  // Evidence
  uploadEvidence: (entityId, controlId, formData) => api.post(`/maturity/evidence/${entityId}/${controlId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getEvidence: (entityId, controlId) => api.get(`/maturity/evidence/${entityId}/${controlId}`),
  deleteEvidence: (fileId) => api.delete(`/maturity/evidence/${fileId}`),
  
  // Snapshots
  createSnapshot: (entityId) => api.post(`/maturity/snapshots/${entityId}`),
  getSnapshots: (entityId) => api.get(`/maturity/snapshots/${entityId}`),
  getAllSnapshots: () => api.get('/maturity/snapshots'),
  
  // Report Generation
  downloadReport: async (entityId) => {
    const response = await api.get(`/export/maturity/${entityId}/docx`, {
      responseType: 'blob'
    });
    return response;
  },

  // DPO Review Queue (Sprint 2)
  getReviewQueue: (entityId, campaignId) => api.get(`/maturity/review-queue/${entityId}`, { params: { campaign_id: campaignId } }),
  getReviewQueueSummary: (entityId, campaignId) => api.get(`/maturity/review-queue/${entityId}/summary`, { params: { campaign_id: campaignId } }),
  confirmControl: (controlId, data) => api.post(`/maturity/controls/${controlId}/confirm`, data),
  getControlHistory: (controlId, entityId) => api.get(`/maturity/controls/${controlId}/history`, { params: { entity_id: entityId } }),

  // Seed (admin only)
  seedData: () => api.post('/maturity/seed')
};

// Risk Configuration
export const riskConfigAPI = {
  getAll: () => api.get('/risk-configs'),
  get: (configId) => api.get(`/risk-configs/${configId}`),
  update: (configId, config) => api.put(`/risk-configs/${configId}`, config),
  reset: (configId) => api.post(`/risk-configs/${configId}/reset`),
};

// Questions
export const questionsAPI = {
  getAll: (category) => api.get('/questions', { params: { category } }),
  getOne: (id) => api.get(`/questions/${id}`),
  create: (data) => api.post('/questions', data),
  update: (id, data) => api.put(`/questions/${id}`, data),
  delete: (id) => api.delete(`/questions/${id}`),
};

// Questionnaires
export const questionnairesAPI = {
  getAll: () => api.get('/questionnaires'),
  getOne: (id) => api.get(`/questionnaires/${id}`),
  create: (data) => api.post('/questionnaires', data),
  update: (id, data) => api.put(`/questionnaires/${id}`, data),
  delete: (id) => api.delete(`/questionnaires/${id}`),
};

// Questionnaire Campaigns
export const campaignsAPI = {
  getAll: () => api.get('/questionnaire-campaigns'),
  getOne: (id) => api.get(`/questionnaire-campaigns/${id}`),
  create: (data) => api.post('/questionnaire-campaigns', data),
  update: (id, data) => api.put(`/questionnaire-campaigns/${id}`, data),
  delete: (id) => api.delete(`/questionnaire-campaigns/${id}`),
};

// Legacy DPIA Assessments (Part 3) - Deprecated, use dpiaAPI instead
export const dpiaLegacyAPI = {
  getAll: (entityId) => api.get('/dpia-assessments', { params: { entity_id: entityId } }),
  getOne: (id) => api.get(`/dpia-assessments/${id}`),
  create: (data) => api.post('/dpia-assessments', data),
  update: (id, data) => api.put(`/dpia-assessments/${id}`, data),
  delete: (id) => api.delete(`/dpia-assessments/${id}`),
  calculateRisk: (id, riskType, inputValues) => api.post(
    `/dpia-assessments/${id}/calculate-risk?risk_type=${riskType}`,
    inputValues
  ),
};

// AI Assessments (Part 4 - NIST AI RMF)
export const aiAssessmentAPI = {
  getAll: (entityId, clientId) => api.get('/ai-assessments', { params: { entity_id: entityId, client_id: clientId } }),
  getOne: (id) => api.get(`/ai-assessments/${id}`),
  create: (data) => api.post('/ai-assessments', data),
  update: (id, data) => api.put(`/ai-assessments/${id}`, data),
  delete: (id) => api.delete(`/ai-assessments/${id}`),
  calculateRisk: (id, riskType, inputValues) => api.post(
    `/ai-assessments/${id}/calculate-risk?risk_type=${riskType}`,
    inputValues
  ),
};

// Users
export const usersAPI = {
  getAll: (clientId, entityId, dpoOnly) => api.get('/users', { params: { client_id: clientId, entity_id: entityId, dpo_only: dpoOnly || undefined } }),
  getOne: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  // Admin endpoints for user creation
  adminCreate: (userData) => api.post('/users/admin/create', userData),
  orgInvite: (inviteData) => api.post('/users/org/invite', inviteData),
  // Invite-only onboarding endpoints
  inviteUser: (userData) => api.post('/users', userData),
  resendInvite: (userId) => api.post(`/users/${userId}/resend-invite`),
  // Email change flow
  requestEmailChange: (userId, newEmail) => api.post(`/users/${userId}/request-email-change`, { new_email: newEmail }),
};

// Audit Logs
export const auditAPI = {
  getAll: (entityId, limit = 50, skip = 0, resourceType) => api.get('/audit-logs', {
    params: {
      entity_id: entityId,
      limit,
      skip,
      ...(resourceType && { resource_type: resourceType })
    }
  }),
};

// Dashboard
export const dashboardAPI = {
  getDPOStats: () => api.get('/dashboard/dpo-stats'),
  getClientStats: () => api.get('/dashboard/client-stats'),
  getMasterControl: () => api.get('/dashboard/master-control'),
  getClientControl: () => api.get('/dashboard/client-control'),
};

// Questionnaire API
export const questionnaireAPI = {
  // Templates
  getTemplates: (status) => api.get('/questionnaires/templates', { params: { status } }),
  getDefaultTemplate: () => api.get('/questionnaires/templates/default'),
  getTemplate: (id) => api.get(`/questionnaires/templates/${id}`),
  createTemplate: (data) => api.post('/questionnaires/templates', data),
  publishTemplate: (id) => api.put(`/questionnaires/templates/${id}/publish`),
  
  // Questions
  getQuestions: (domain) => api.get('/questionnaires/questions', { params: { domain } }),
  getQuestion: (id) => api.get(`/questionnaires/questions/${id}`),
  getAnswerOptions: () => api.get('/questionnaires/answer-options'),
  getDomainWeights: () => api.get('/questionnaires/domain-weights'),
  
  // Single Instances
  getInstances: (entityId, clientId, departmentId, status) => api.get('/questionnaires/instances', { params: { entity_id: entityId, client_id: clientId, department_id: departmentId, status } }),
  getInstance: (id) => api.get(`/questionnaires/instances/${id}`),
  createInstance: (data) => api.post('/questionnaires/instances', data),
  sendInstance: (id) => api.put(`/questionnaires/instances/${id}/send`),
  
  // Campaigns
  getCampaigns: (entityId, clientId, departmentId, status) => api.get('/questionnaires/campaigns', { params: { entity_id: entityId, client_id: clientId, department_id: departmentId, status } }),
  getCampaign: (id) => api.get(`/questionnaires/campaigns/${id}`),
  createCampaign: (data) => api.post('/questionnaires/campaigns', data),
  addRecipients: (campaignId, recipients) => api.post(`/questionnaires/campaigns/${campaignId}/recipients`, recipients),
  removeRecipient: (campaignId, recipientId) => api.delete(`/questionnaires/campaigns/${campaignId}/recipients/${recipientId}`),
  preflightCampaign: (id) => api.get(`/questionnaires/campaigns/${id}/send-preflight`),
  sendCampaign: (id) => api.put(`/questionnaires/campaigns/${id}/send`),
  closeCampaign: (id) => api.put(`/questionnaires/campaigns/${id}/close`),
  finalizeCampaign: (id) => api.put(`/questionnaires/campaigns/${id}/finalize`),
  
  // Responses
  saveResponses: (responses, instanceId, campaignId, recipientId, isDraft = true) => 
    api.post('/questionnaires/responses/save', responses, { 
      params: { instance_id: instanceId, campaign_id: campaignId, recipient_id: recipientId, is_draft: isDraft } 
    }),
  getResponses: (instanceId, campaignId, recipientId) => 
    api.get('/questionnaires/responses', { 
      params: { instance_id: instanceId, campaign_id: campaignId, recipient_id: recipientId } 
    }),
  submitResponses: (instanceId, campaignId, recipientId) => 
    api.post('/questionnaires/responses/submit', null, { 
      params: { instance_id: instanceId, campaign_id: campaignId, recipient_id: recipientId } 
    }),
  
  // Scoring
  getScore: (objectType, objectId) => api.get(`/questionnaires/score/${objectType}/${objectId}`),
  recomputeScore: (objectType, objectId) => api.post(`/questionnaires/score/${objectType}/${objectId}/recompute`),
  
  // Reconciliation
  getReconciliationStatus: (campaignId) => api.get(`/questionnaires/campaigns/${campaignId}/reconciliation`),
  getResponsesByQuestion: (campaignId, questionId) => 
    api.get(`/questionnaires/campaigns/${campaignId}/responses-by-question`, { params: { question_id: questionId } }),
  createOverride: (campaignId, data) => api.post(`/questionnaires/campaigns/${campaignId}/override`, data),
  
  // Audit
  getAuditLogs: (objectType, objectId) => api.get(`/questionnaires/audit/${objectType}/${objectId}`),
  
  // Email
  getEmailStatus: () => api.get('/questionnaires/email/status'),
  testEmail: (recipientEmail, recipientName = 'Test User') => 
    api.post(`/questionnaires/email/test?recipient_email=${encodeURIComponent(recipientEmail)}&recipient_name=${encodeURIComponent(recipientName)}`),
  sendReminders: (campaignId) => api.post(`/questionnaires/campaigns/${campaignId}/send-reminders`),
  
  // Public (token-based)
  getByToken: (token) => api.get(`/questionnaires/respond/${token}`),
  saveByToken: (token, responses, isDraft = true) => 
    api.post(`/questionnaires/respond/${token}/save`, responses, { params: { is_draft: isDraft } }),
  submitByToken: (token) => api.post(`/questionnaires/respond/${token}/submit`),
};

// Readiness Assessment API
export const readinessAPI = {
  // Consultant endpoints
  getAll: (params) => api.get('/readiness-assessments', { params }),
  getById: (id) => api.get(`/readiness-assessments/${id}`),
  create: (data) => api.post('/readiness-assessments', data),
  update: (id, data) => api.put(`/readiness-assessments/${id}`, data),
  delete: (id) => api.delete(`/readiness-assessments/${id}`),
  void: (id) => api.post(`/readiness-assessments/${id}/void`),
  send: (id) => api.post(`/readiness-assessments/${id}/send`),
  sendReminder: (id) => api.post(`/readiness-assessments/${id}/send-reminder`),
  exportCsv: (id) => api.get(`/readiness-assessments/${id}/export/csv`, { responseType: 'blob' }),
  
  // Public endpoints
  // Public (token-authenticated) — use publicApi so respondents without a platform login
  // don't get 401-redirected to /login.
  getPublic: (token) => publicApi.get(`/readiness-assessments/public/${token}`),
  getPublicResponses: (token) => publicApi.get(`/readiness-assessments/public/${token}/responses`),
  saveResponse: (token, data) => publicApi.post(`/readiness-assessments/public/${token}/responses`, data),
  saveResponsesBulk: (token, responses) => publicApi.post(`/readiness-assessments/public/${token}/responses/bulk`, { responses }),
  submit: (token) => publicApi.post(`/readiness-assessments/public/${token}/submit`),
};

// Pre-Screen Admin API (DPO authenticated)
export const prescreenAdminAPI = {
  getAll: (params) => api.get('/prescreen', { params }),
  getOne: (id) => api.get(`/prescreen/${id}`),
  overrideOutcome: (id, outcome, rationale) =>
    api.put(`/prescreen/${id}/override-outcome`, null, {
      params: { override_outcome: outcome, override_rationale: rationale },
    }),
  createIntake: (id) => api.post(`/prescreen/${id}/intake`),
  delete: (id) => api.delete(`/prescreen/${id}`),
  getStats: () => api.get('/prescreen/stats/summary'),
  listTokens: (params) => api.get('/prescreen/tokens', { params }),
};

// Email Suppression API
export const emailSuppressionAPI = {
  list: (reason) => api.get('/email-suppression', { params: reason ? { reason } : {} }),
  stats: () => api.get('/email-suppression/stats'),
  remove: (email) => api.delete(`/email-suppression/${encodeURIComponent(email)}`),
  bouncedUsers: () => api.get('/email-suppression/bounced-users'),
};

// Temp Guest API
export const tempGuestAPI = {
  // DPO management
  create: (data) => api.post('/temp-guests', data),
  list: (params) => api.get('/temp-guests', { params }),
  revoke: (guestId) => api.delete(`/temp-guests/${guestId}`),
  extend: (guestId, extendDays) => api.put(`/temp-guests/${guestId}/extend`, { extend_days: extendDays }),
  resendInvite: (guestId) => api.post(`/temp-guests/${guestId}/resend-invite`),
  // Guest auth (unauthenticated — uses publicApi)
  probeAuthMethod: (email) => publicApi.post('/temp-guests/auth/probe', { email }),
  requestOtp: (email) => publicApi.post('/temp-guests/auth/otp/request', { email }),
  verifyOtp: (email, code) => publicApi.post('/temp-guests/auth/otp/verify', { email, code }),
  passwordLogin: (email, password) => publicApi.post('/temp-guests/auth/login', { email, password }),
  // Guest profile — fresh modules list (authenticated as temp_guest)
  myProfile: () => api.get('/temp-guests/me/profile'),
  // Guest submission history (authenticated as temp_guest)
  mySubmissions: () => api.get('/temp-guests/me/submissions'),
  // Guest DPIA intake: load saved state + auto-save
  getDpia: () => api.get('/temp-guests/me/dpia-intake'),
  saveDpia: (data) => api.put('/temp-guests/me/dpia-intake/save', data),
  getBreach: () => api.get('/temp-guests/me/breach-intake'),
  saveBreach: (data) => api.put('/temp-guests/me/breach-intake/save', data),
  // Guest questionnaire: load + auto-save
  getQuestionnaire: () => api.get('/temp-guests/me/questionnaire'),
  saveQuestionnaire: (data) => api.put('/temp-guests/me/questionnaire/save', data),
  // Guest form submissions (authenticated as temp_guest)
  submitRopa: (data) => api.post('/temp-guests/submit/ropa-intake', data),
  submitBreach: (data) => api.post('/temp-guests/submit/breach-intake', data),
  submitDpia: (data) => api.post('/temp-guests/submit/dpia-intake', data),
  submitPrescreen: (data) => api.post('/temp-guests/submit/prescreen', data),
  submitReadiness: (data) => api.post('/temp-guests/submit/readiness-assessment', data),
  submitQuestionnaire: (data) => api.post('/temp-guests/submit/questionnaire', data),
};

export default api;
